import { useState } from 'react';
import {
  FormControlLabel,
  Switch,
  Typography,
  Box,
  SxProps,
  Theme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from '@mui/material';
import { formatDistanceToNow } from 'date-fns';

export interface EntityEnabledToggleProps {
  /** true when the entity is enabled (not disabled). */
  enabled: boolean;
  /** Reason shown when disabled (from disabledReason). */
  disabledReason?: string | null;
  /** When the entity was disabled (from disabledAt) — shown as relative time. */
  disabledAt?: string | null;
  /** Disable the switch while a toggle is persisting. */
  busy?: boolean;
  /**
   * Called when the user flips the switch. Parent persists.
   * On disable, `reason` carries the operator-supplied note.
   */
  onToggle: (nextEnabled: boolean, reason?: string) => void;
  size?: 'small' | 'medium';
  /** Optional wrapper sx override/extension. */
  sx?: SxProps<Theme>;
}

/** "an hour ago" style label, with date-fns' "about"/"over"/"almost" prefixes trimmed. */
function relativeTime(iso: string): string {
  return formatDistanceToNow(new Date(iso), { addSuffix: true })
    .replace(/^about /, '')
    .replace(/^over /, '')
    .replace(/^almost /, '');
}

/**
 * Full-width enable/disable bar. Switch sits on the left over the row background;
 * when disabled the whole row turns red with white text explaining the status.
 * Disabling opens a dialog to capture a reason; enabling flips immediately.
 */
export function EntityEnabledToggle({
  enabled,
  disabledReason,
  disabledAt,
  busy = false,
  onToggle,
  size = 'small',
  sx,
}: EntityEnabledToggleProps) {
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reasonDraft, setReasonDraft] = useState('');

  const handleSwitch = (next: boolean) => {
    if (next) {
      onToggle(true);
    } else {
      // Disabling: collect a reason first.
      setReasonDraft('');
      setReasonOpen(true);
    }
  };

  const confirmDisable = () => {
    const reason = reasonDraft.trim() || 'Manually disabled';
    setReasonOpen(false);
    onToggle(false, reason);
  };

  return (
    <>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 1.5,
          py: 0.75,
          borderRadius: 1,
          bgcolor: enabled ? 'transparent' : 'error.main',
          color: enabled ? 'text.primary' : 'error.contrastText',
          transition: 'background-color 0.2s',
          ...sx,
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              disabled={busy}
              onChange={e => handleSwitch(e.target.checked)}
              size={size}
            />
          }
          label={enabled ? 'Enabled' : 'Disabled'}
          sx={{ m: 0, color: 'inherit', flexShrink: 0 }}
        />
        {!enabled && (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            🚫 {disabledReason || 'Disabled'}
            {disabledAt && (
              <Typography
                component="span"
                variant="body2"
                sx={{ fontWeight: 400, opacity: 0.85, ml: 0.75 }}
              >
                · disabled {relativeTime(disabledAt)}
              </Typography>
            )}
          </Typography>
        )}
      </Box>

      <Dialog
        open={reasonOpen}
        onClose={() => setReasonOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Disable — reason</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            margin="dense"
            label="Why is this being disabled?"
            placeholder="e.g. bad script, needs regen"
            value={reasonDraft}
            onChange={e => setReasonDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                confirmDisable();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReasonOpen(false)}>Cancel</Button>
          <Button onClick={confirmDisable} color="error" variant="contained">
            Disable
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
